import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList as List } from "react-window";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

// Material UI
import { Box, Button } from "@mui/material";

// Custom Components
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import DualList from "../../components/DualList/DualList";
import AdminPageLayout from "../../components/FullPageLayout/AdminPageLayout";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import UserFilters from "./ManageGroupMembersFilters";
import {
  enqueueSuccessSnackbar,
  enqueueErrorSnackbar,
  enqueueInfoSnackbar,
  closeSnackbar
} from "../../redux/actions/snackbarActions";
// Hooks
import { useUsers } from "../../hooks/apiHooks/users";
import { useGroups } from "../../hooks/apiHooks/groups";
import useResource from "../../hooks/useResource";
import { formatDate } from "../../utils/datetime";

const USER_JSONAPI_REQUEST = {
  fields: {
    user: ["fullName", "hireDate", "location", "position", "groupsUsers"],
    location: ["name"],
    position: ["name"],
    groupsUser: ["groupId", "extendedSourceType"]
  },
  include: ["location", "position", "groupsUsers"]
};

const FILTERED_USERS_JSONAPI_REQUEST = {
  fields: {
    user: ["fullName"]
  },
  sort: "fullName"
};

const ManageGroupMembers = ({ group, goBack }) => {
  const [allUsers, setAllUsers] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedIdUserMap, setSelectedIdUserMap] = useState(null);
  const [unselectedIdUserMap, setUnselectedIdUserMap] = useState(null);
  const [selectedList, setSelectedList] = useState([]);
  const [unselectedList, setUnSelectedList] = useState([]);
  const [filters, setFilters] = useState({
    active: true,
    divisionId: group?.customer?.id
  });

  const { t } = useTranslation();

  const dispatch = useDispatch();

  const { getUsers } = useUsers();
  const { updateGroup } = useGroups();

  const apiCallbacks = useMemo(
    () => ({
      getUsers: (...params) => getUsers(...params)
    }),
    []
  );
  const [
    { data: filteredUsers },
    { getUsers: search },
    filterAction
  ] = useResource(apiCallbacks);

  /**
   * Returns an object containing the properties that will be passed into
   * the DualListItem component. Hide arrow should be true if the employee
   * is added to group through location and/or position
   */
  const getListItemDetails = (item, type, hideArrow) => {
    return {
      id: item.id,
      name: item.fullName,
      subtitles: type
        ? [
            item.position?.name || "",
            item.location?.name || "",
            t(`manageGroupMembers.${type}`)
          ]
        : [item.position?.name || "", item.location?.name || ""],
      hideArrow
    };
  };

  const recalculateLists = () => {
    const selectedList = [];
    const unselectedList = [];
    selectedIdUserMap.forEach(value => selectedList.push(value));
    filteredUsers.forEach(user => {
      if (unselectedIdUserMap.has(user.id)) {
        unselectedList.push(unselectedIdUserMap.get(user.id));
      }
    });
    setSelectedList(selectedList);
    setUnSelectedList(unselectedList);
  };

  useEffect(() => {
    if (allUsers !== null) {
      search({ filters, ...FILTERED_USERS_JSONAPI_REQUEST });
    }
  }, [allUsers, filters]);

  useEffect(() => {
    if (filteredUsers) {
      recalculateLists();
    }
  }, [filteredUsers]);

  useEffect(() => {
    if (!group || !group.id) {
      return;
    }
    (async () => {
      const { data: users } = await getUsers({
        filters: { active: true, divisionId: group.customer?.id },
        custom: { relationToGroupId: group.id },
        ...USER_JSONAPI_REQUEST
      });

      const selectedIdsMap = new Map();
      const unselectedIdsMap = new Map();
      users.forEach(user => {
        if (
          user.groupsUsers.find(
            groupUser => `${groupUser.groupId}` === group.id
          )
        ) {
          const groupUserType = user.groupsUsers[0].extendedSourceType;
          selectedIdsMap.set(
            user.id,
            getListItemDetails(
              user,
              groupUserType,
              groupUserType !== "employee"
            )
          );
        } else {
          unselectedIdsMap.set(user.id, getListItemDetails(user, "", false));
        }

        setSelectedIdUserMap(selectedIdsMap);
        setUnselectedIdUserMap(unselectedIdsMap);
      });

      setAllUsers(users);
    })();
  }, [(group || {}).id]);

  const loading = allUsers === null;
  const isUpdating = isSaving || filterAction !== null;

  const handleAddAll = () => {
    unselectedList.forEach(item => {
      item.subtitles.push(t(`manageGroupMembers.justAdded`));
      unselectedIdUserMap.delete(item.id);
      selectedIdUserMap.set(item.id, item);
    });
    recalculateLists();
  };

  const handleRemoveAll = () => {
    selectedList.forEach(item => {
      if (item.hideArrow) return;
      item.subtitles.pop();
      selectedIdUserMap.delete(item.id);
      unselectedIdUserMap.set(item.id, item);
    });
    recalculateLists();
  };

  const handleAdd = item => {
    item.subtitles.push(t(`manageGroupMembers.justAdded`));
    unselectedIdUserMap.delete(item.id);
    selectedIdUserMap.set(item.id, item);
    recalculateLists();
  };

  const handleRemove = item => {
    item.subtitles.pop();
    selectedIdUserMap.delete(item.id);
    unselectedIdUserMap.set(item.id, item);
    recalculateLists();
  };

  const applyFilters = filters => {
    setFilters({
      ...(filters.term && { fullName: filters.term }),
      ...(filters.position && { positions: filters.position }),
      ...(filters.location && { locations: filters.location }),
      ...(filters.state && { states: filters.state }),
      ...(filters.hiredSince && {
        hireDateAfter: formatDate(filters.hiredSince, "YYYY-MM-DD")
      }),
      ...(filters.hiredBefore && {
        hireDateBefore: formatDate(filters.hiredBefore, "YYYY-MM-DD")
      }),
      divisionId: group.customer?.id,
      active: true
    });
  };

  const save = () => {
    setIsSaving(true);
    const waitingSnackbar = dispatch(
      enqueueInfoSnackbar({
        message: t("manageGroupMembers.messages.savingWait"),
        options: { autoHideDuration: null }
      })
    );
    updateGroup(group.id, {
      action: "update_members",
      user_ids: [...selectedIdUserMap.keys()]
    })
      .then(response => {
        if (response.errors) throw response.errors;
        if (waitingSnackbar)
          dispatch(closeSnackbar(waitingSnackbar.notification.key));
        dispatch(
          enqueueSuccessSnackbar({
            message: t("administration.groupInformation.manageGroupSuccessMsg")
          })
        );
        goBack();
      })
      .catch(error => {
        if (waitingSnackbar)
          dispatch(closeSnackbar(waitingSnackbar.notification.key));
        dispatch(
          enqueueErrorSnackbar({
            message: t("manageGroupMembers.messages.error")
          })
        );
      });
  };

  const cancel = () => {
    goBack();
  };

  return (
    <AdminPageLayout
      title={
        <Breadcrumb
          routes={[
            t(`administration.groups`),
            group?.name,
            t("manageGroupMembers.title")
          ]}
        />
      }
      onGoBackClick={goBack}
    >
      <Box display="flex" my={4} flexDirection="column" alignItems="stretch">
        <UserFilters
          onFilter={filters => applyFilters(filters)}
          group={group}
        />

        {loading ? (
          <Box pt={5} pb={15}>
            <LoadingSpinner />
          </Box>
        ) : (
          <>
            <DualList
              renderList={(items, DualListItem) => (
                <Box flex={1} minHeight={460} mt={3}>
                  <AutoSizer>
                    {({ height, width }) => (
                      <List
                        className="List"
                        height={height}
                        itemCount={items.length}
                        itemSize={109}
                        width={width}
                      >
                        {({ index, style }) => {
                          return (
                            <div style={style}>
                              {DualListItem(items[index])}
                            </div>
                          );
                        }}
                      </List>
                    )}
                  </AutoSizer>
                </Box>
              )}
              disabled={isUpdating}
              onAddAll={handleAddAll}
              removeAllButtonText={t("manageGroupMembers.list.removeAllBtn")}
              addAllButtonText={t("dualList.addAllBtn")}
              onRemoveAll={handleRemoveAll}
              onAddToSelectedList={handleAdd}
              onAddToUnselectedList={handleRemove}
              selectedListHeader={t(
                "manageGroupMembers.list.includedEmployees"
              )}
              unselectedListHeader={t(
                "manageGroupMembers.list.availableEmployees"
              )}
              selectedList={selectedList}
              unselectedList={unselectedList}
            />
          </>
        )}
        <Box mt={4} display="flex" justifyContent="flex-end">
          <ButtonGroup>
            <Button color="primary" onClick={cancel}>
              {t("cancelBtn")}
            </Button>
            <Button variant="contained" color="primary" onClick={save}>
              {t("saveBtn")}
            </Button>
          </ButtonGroup>
        </Box>
      </Box>
    </AdminPageLayout>
  );
};

ManageGroupMembers.propTypes = {
  group: PropTypes.shape({
    id: PropTypes.string,
    customer: PropTypes.shape({
      id: PropTypes.string
    })
  }),
  goBack: PropTypes.func
};

export default ManageGroupMembers;
